<template>
    <v-card flat outlined class="hodometros-viagem">
        <v-card-title class="text-subtitle-1 py-2">
            <v-icon small class="mr-2">mdi-speedometer</v-icon>
            {{ $t('hodometroViagem.title') }}
            <v-spacer></v-spacer>
            <v-progress-circular v-if="loading" indeterminate size="18" width="2"></v-progress-circular>
        </v-card-title>
        <v-card-text v-if="!idViagemGrid" class="text-caption">
            {{ $t('hodometroViagem.newTrip') }}
        </v-card-text>
        <v-card-text v-else-if="info" class="pt-0">
            <v-row dense>
                <v-col v-for="fonte in fontes" :key="fonte.id" cols="12" sm="6">
                    <div class="hodometro-fonte pa-2" :class="{ 'hodometro-fonte--usada': fonteUsada === fonte.id }">
                        <div class="d-flex align-center mb-1">
                            <span class="font-weight-bold">{{ fonte.label }}</span>
                            <v-chip v-if="fonteUsada === fonte.id" x-small color="primary" class="ml-2">
                                {{ $t('hodometroViagem.used') }}
                            </v-chip>
                        </div>
                        <div class="d-flex justify-space-between text-body-2">
                            <span class="grey--text text--darken-1">{{ $t('hodometroViagem.initial') }}</span>
                            <span>{{ formata(fonte.inicial) }}</span>
                        </div>
                        <div class="d-flex justify-space-between text-body-2">
                            <span class="grey--text text--darken-1">{{ $t('hodometroViagem.final') }}</span>
                            <span>{{ formata(fonte.final) }}</span>
                        </div>
                        <div class="d-flex justify-space-between text-body-2">
                            <span class="grey--text text--darken-1">{{ $t('hodometroViagem.km') }}</span>
                            <span class="font-weight-bold">{{ formata(fonte.km) }}</span>
                        </div>
                    </div>
                </v-col>
            </v-row>
            <div class="d-flex flex-wrap align-center mt-2 text-body-2">
                <span class="mr-2">
                    {{ $t('hodometroViagem.preference') }}:
                    <strong>{{ preferenciaVirtual ? $t('hodometroViagem.virtual') : $t('hodometroViagem.can') }}</strong>
                    ({{ $t('hodometroViagem.origin.' + origemExibida) }})
                </span>
                <v-chip v-if="info.km_aferido != null" x-small outlined class="mr-1">
                    {{ $t('hodometroViagem.kmUsed', { km: formata(info.km_aferido) }) }}
                </v-chip>
                <v-chip v-if="info.km_aferido_manual != null" x-small outlined color="warning" class="mr-1">
                    {{ $t('hodometroViagem.manualKm', { km: formata(info.km_aferido_manual) }) }}
                </v-chip>
                <v-chip v-if="ajustadoAoNominal" x-small outlined class="mr-1">
                    {{ $t('hodometroViagem.nominalAdjusted', { km: formata(info.km_nominal) }) }}
                </v-chip>
            </div>
            <div v-if="semLeituras" class="text-caption grey--text mt-1">
                {{ $t('hodometroViagem.noReadings') }}
            </div>
            <v-switch v-if="editavel" v-model="preferenciaVirtual" dense class="mt-2" persistent-hint
                :label="$t('hodometroViagem.useVirtual')" :hint="hintPreferencia"
                :disabled="disabled || info.mes_fechado == 1"></v-switch>
        </v-card-text>
    </v-card>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';

const FONTE_CAN = 1;
const FONTE_VIRTUAL = 2;

/**
 * Hodômetros da viagem do grid: leituras e km de CAN e virtual, a fonte usada no km aferido
 * e a preferência efetiva (viagem > vigência do veículo > CAN). Com `editavel`, mostra o
 * switch da preferência; a tela que salva a viagem lê `alteracaoPreferencia()`.
 */
export default {
    name: 'HodometrosViagemInfo',
    props: {
        idViagemGrid: { type: [Number, String], default: null },
        editavel: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
    },
    data: () => ({
        info: null,
        loading: false,
        preferenciaVirtual: false,
        preferenciaVirtualOriginal: false,
    }),
    computed: {
        fontes() {
            return [
                { id: FONTE_CAN, label: this.$t('hodometroViagem.can'), inicial: this.info.hodometro_can_inicial, final: this.info.hodometro_can_final, km: this.info.km_aferido_can },
                { id: FONTE_VIRTUAL, label: this.$t('hodometroViagem.virtual'), inicial: this.info.hodometro_virtual_inicial, final: this.info.hodometro_virtual_final, km: this.info.km_aferido_virtual },
            ];
        },
        fonteUsada() {
            return this.info && this.info.fonte_km_aferido != null ? Number(this.info.fonte_km_aferido) : null;
        },
        origemExibida() {
            if (this.preferenciaVirtual !== this.preferenciaVirtualOriginal) return 'viagem';
            return this.info.origem_preferencia_hodometro || 'padrao';
        },
        hintPreferencia() {
            if (this.origemExibida === 'vigencia') return this.$t('hodometroViagem.hintVigencia');
            if (this.origemExibida === 'viagem' && this.info.nome_usuario_preferencia_hodometro) {
                return this.$t('hodometroViagem.hintViagem', {
                    usuario: this.info.nome_usuario_preferencia_hodometro,
                    data: this.info.dt_preferencia_hodometro_extenso,
                });
            }
            return this.origemExibida === 'padrao' ? this.$t('hodometroViagem.hintPadrao') : '';
        },
        ajustadoAoNominal() {
            const i = this.info;
            return i.km_aferido != null && i.km_nominal != null && Number(i.km_aferido) === Number(i.km_nominal) &&
                Number(i.km_aferido) !== Number(i.km_aferido_can) && Number(i.km_aferido) !== Number(i.km_aferido_virtual);
        },
        semLeituras() {
            const i = this.info;
            return !i.hodometro_can_inicial && !i.hodometro_can_final && !i.hodometro_virtual_inicial && !i.hodometro_virtual_final;
        },
    },
    watch: {
        idViagemGrid: {
            immediate: true,
            handler() {
                this.carregar();
            },
        },
    },
    methods: {
        async carregar() {
            this.info = null;
            if (!this.idViagemGrid) return;
            this.loading = true;
            try {
                const response = await apiController.request(apiController.endpoints.getHodometrosviagemgrid, {
                    params: { idviagemgrid: this.idViagemGrid },
                    silent: true,
                });
                if (!response.data || response.data.erro) {
                    throw new Error(response.data && response.data.mensagem);
                }
                this.info = response.data;
                this.preferenciaVirtual = Number(response.data.hodometro_virtual_efetivo) === 1;
                this.preferenciaVirtualOriginal = this.preferenciaVirtual;
            } catch (e) {
                EventBus.$emit('show-alert', { mensagem: this.$t('hodometroViagem.loadError'), tipo: 'warning' });
            } finally {
                this.loading = false;
            }
        },
        /** Campos do payload de updateviagemgrid quando o usuário mexeu no switch; senão {}. */
        alteracaoPreferencia() {
            if (!this.info || this.preferenciaVirtual === this.preferenciaVirtualOriginal) return {};
            return {
                alterar_preferencia_hodometro: 1,
                preferencia_hodometro_virtual: this.preferenciaVirtual ? 1 : 0,
            };
        },
        /** Preferência em vigor na tela (com a alteração ainda não salva). */
        prefereVirtual() {
            return this.preferenciaVirtual;
        },
        formata(valor) {
            if (valor == null || valor === '') return '—';
            const n = Number(valor);
            return isNaN(n) ? valor : n.toLocaleString(this.$i18n.locale, { maximumFractionDigits: 2 });
        },
    },
};
</script>

<style scoped>
.hodometro-fonte {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    height: 100%;
}

.hodometro-fonte--usada {
    border-color: var(--v-primary-base);
    border-width: 2px;
}
</style>
